import React from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ShoppingCart, Mail, Gamepad2 } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      title: 'Make an Order',
      description: 'Browse our extensive collection of premium gaming products and select the one that best suits your needs. Choose your preferred subscription duration and add the product to your cart. Complete the checkout process using our secure payment system with either credit card or PayPal.',
      icon: ShoppingCart
    },
    {
      number: '02',
      title: 'Receive Email',
      description: 'Upon successful payment confirmation, you will immediately receive a comprehensive email containing your product download link, installation instructions, and activation key. Our automated system ensures instant delivery to your registered email address, typically within 1-2 minutes of purchase completion.',
      icon: Mail
    },
    {
      number: '03',
      title: 'Install and Play',
      description: 'Follow the detailed step-by-step instructions provided in your email to download and install the product. Activate your purchase using the unique key provided, and you are ready to experience enhanced gameplay. Our products are designed for seamless integration and optimal performance.',
      icon: Gamepad2
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-orange-600/20 text-orange-400 border-orange-500/30 mb-4">
            Process
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How It Works
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Our streamlined process ensures you can start enhancing your gaming experience within minutes. Follow these simple steps to get started.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Card 
                key={index}
                className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10 hover:border-purple-500/50 transition-all duration-300 relative overflow-hidden group"
              >
                {/* Step Number Background */}
                <div className="absolute top-0 right-0 text-[120px] font-bold text-white/5 leading-none pr-4 pt-2 group-hover:text-purple-500/10 transition-colors">
                  {step.number}
                </div>

                <CardContent className="p-8 relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-purple-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-600/30 transition-colors">
                    <Icon className="text-purple-400" size={32} />
                  </div>

                  {/* Step Number */}
                  <div className="flex items-center mb-4">
                    <span className="text-purple-400 font-bold text-sm mr-3">STEP {step.number}</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-500/50 to-transparent" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30 inline-block">
            <CardContent className="p-6">
              <p className="text-gray-300">
                <span className="text-purple-400 font-semibold">Need assistance?</span> Our dedicated support team is available 24/7 to help you with any questions or technical issues you may encounter.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
